import {objectFromEntries} from '@benchling/js-utils';
import React from 'react';

import {TYPES_TO_DISPLAY_NAMES} from 'analysis-tool/AnalysisToolConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FilterStepFilterConfig from 'data-frames/filters/components/FilterStepFilterConfig';
import findAndAssertColumn from 'data-frames/util/findAndAssertColumn';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import AnalysisFormulaEditor from 'pipelines/formula/components/AnalysisFormulaEditor';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';
import IsInputXToggleGroup from 'pipelines/interpolation/IsInputXToggleGroup';
import {
  GetPipelineStepShape,
  GetTaggedUnionTagValues,
  PipelineJsonSpecToTsType,
} from 'pipelines/PipelineTypes';
import {PipelineConfigJsonSpec, StepConfigShape} from 'pipelines/StepShapeUtils';
import UserDefinedFunctionEditor from 'pipelines/user-defined-function/UserDefinedFunctionEditor';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {capitalize, constantCaseToWords} from 'util/NameUtil';
import {JSONValue} from 'util/Types';

import ArrayEditor from '../editors/ArrayEditor';
import ColumnDragAndDrop from '../editors/ColumnDragAndDrop';
import ApplyColumnMappingEditor from '../editors/custom-editors/ApplyColumnMappingEditor';
import {
  ConnectionSelector,
  ConnectionFileSelector,
  ConnectionTransformDataTypeSelector,
} from '../editors/custom-editors/ConnectionDataEditor';
import {DataSourceTypeEditor, DataSourceEditor} from '../editors/custom-editors/CreateProcedureDatasetEditor';
import {DashboardBlockEditor} from '../editors/custom-editors/DashboardBlockEditor';
import DefineTemplateVariablesEditor from '../editors/custom-editors/DefineTemplateVariablesEditor';
import JoinEditor from '../editors/custom-editors/JoinEditor';
import LookUpConfigSectionEditor from '../editors/custom-editors/LookupColumnAndBy';
import LookUpFieldEditor from '../editors/custom-editors/LookUpFieldEditor';
import PlateSchemaQueryEditor from '../editors/custom-editors/PlateSchemaQueryEditor';
import RegistrySchemaQueryEditor from '../editors/custom-editors/RegistrySchemaQueryEditor';
import SeedItemsEditor from '../editors/custom-editors/SeedItemsEditor';
import UnionEditor from '../editors/custom-editors/UnionEditor';
import SearchEditor from '../editors/SearchEditor';
// eslint-disable-next-line import-access/jsdoc -- ShapeEditor is package-private but step-config-settings is part of the same package
import ShapeEditor from '../editors/ShapeEditor';
import {
  BackendFieldGeneratorConfig,
  BANNER_KEY,
  ConfigDataFrameInfo,
  ConfigDataFramesInfos,
  ConfigShapePath,
  CustomComponentEditorProps,
  CUSTOM_COMPONENT_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  BACKEND_FIELD_GENERATOR_CONFIG,
  REQUIRES_MANUAL_PREVIEW,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  ShapeUISchema,
  TAB_ORDER_UI_SCHEMA_KEY,
  NUM_COLUMNS_KEY,
  StepConfigEditorOverride,
  TaggedUnionFieldSettings,
} from '../StepConfigEditorTypes';
import {BAR_CHART_CONFIG_SECTIONS, BAR_CHART_STYLE_SECTIONS} from './chart-schemas/BarChartSchema';
import {BOX_PLOT_CONFIG_SECTIONS, BOX_PLOT_STYLE_SECTIONS} from './chart-schemas/BoxPlotSchema';
import {
  createChartTypeSchema,
  onChangeColorPaletteMiddleware,
  getDefaultSeriesColorConfig,
} from './chart-schemas/ChartSchemaHelpers';
import {HEATMAP_CONFIG_SECTIONS, HEATMAP_STYLE_SECTIONS} from './chart-schemas/HeatmapSchema';
import {LINE_CHART_CONFIG_SECTIONS, LINE_CHART_STYLE_SECTIONS} from './chart-schemas/LineChartSchema';
import {PIE_CHART_CONFIG_SECTIONS, PIE_CHART_STYLE_SECTIONS} from './chart-schemas/PieChartSchema';
import {
  SCATTER_CHART_CONFIG_SECTIONS,
  SCATTER_CHART_STYLE_SECTIONS,
} from './chart-schemas/ScatterChartSchema';
import {getConfigShapeAtConfigShapePath} from './StepConfigSettingsUtils';

type OnChangeConfigMiddlewareFn<
  JsonSpec extends PipelineConfigJsonSpec,
  StepType extends PipelineStepType = PipelineStepType,
  StepVersion extends number = number,
> = (
  newConfig: PipelineJsonSpecToTsType<JsonSpec, 'draft'>,
  oldConfig: PipelineJsonSpecToTsType<JsonSpec, 'draft'>,
  dataFramesInfos: ConfigDataFramesInfos<StepType, StepVersion>
) => PipelineJsonSpecToTsType<JsonSpec, 'draft'>;

interface SharedComponentUISchema {
  displayName?: string;
  infoText?: string;
}

interface SharedComponentUISchemaV2 extends SharedComponentUISchema {
  configShapePath?: Array<string>;
}

interface StandardComponentUISchemaV2<
  JsonSpec extends PipelineConfigJsonSpec,
> extends SharedComponentUISchemaV2 {
  fieldSettings?: _GetJsonSpecUISchemaShapeV2<JsonSpec>;
}

type _GetJsonSpecUISchemaShapeV2<JsonSpec extends PipelineConfigJsonSpec> = JsonSpec extends {
  kind: 'shape';
  fields: infer Fields extends Record<string, PipelineConfigJsonSpec>;
}
  ? {
      // Only variable names present in this array will be rendered.
      [FIELD_ORDER_UI_SCHEMA_KEY]: ReadonlyArray<keyof Fields>;
      [SECTION_NAME_UI_SCHEMA_KEY]?: string;
    } & {
      [K in keyof Fields]: StandardComponentUISchemaV2<Fields[K]>;
    }
  : JsonSpec extends {kind: 'taggedUnion'}
    ? TaggedUnionFieldSettings
    : never;

export interface TabContainerUISchema {
  displayName: string;
  uiSchema: ShapeUISchema;
}

export interface TabbedUISchema {
  [TAB_ORDER_UI_SCHEMA_KEY]: ReadonlyArray<string>;
  [BACKEND_FIELD_GENERATOR_CONFIG]?: BackendFieldGeneratorConfig;
  [REQUIRES_MANUAL_PREVIEW]?: boolean;
  [key: string]: TabContainerUISchema;
}

/**
 * Get the UI schema type for a step config shape.
 *
 * Recursively traverses the step config shape and returns a UI schema based on the types of
 * encountered specs:
 *   * `kind: 'shape'` -> `{[FIELD_ORDER_UI_SCHEMA_KEY]: [...]; fieldName: {displayName, configShapePath, ...}}`
 *   * `kind: 'array'` -> Recursively applied to the array's elementSpec
 *   * `kind: 'taggedUnion'` -> `TaggedUnionFieldSettings`
 *   * `kind: 'enum'` -> `{displayNames: Record<EnumChoice, string>}`
 *   * Anything else -> `never` (no UI schema supported; these are the leaves of the tree)
 *
 * Each field in a shape gets a `StandardComponentUISchemaV2` which includes `displayName`,
 * `configShapePath`, optional `fieldSettings`, `elementUISchema` (for arrays), and more.
 *
 * Also supports specifying a custom component or a tabbed layout for the step.
 *
 * See examples below in STEP_UI_SCHEMAS.
 */
export type StepTypeConfigUISchemaV2<
  StepType extends PipelineStepType,
  StepVersion extends number,
> = GetPipelineStepShape<StepType, StepVersion>['config'] extends StepConfigShape
  ? ShapeUISchema | TabbedUISchema
  : never;

const AGGREGATION_ELEMENT_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.AGGREGATE_DATASET][0].config.aggregations.elementSpec;

const WINDOW_ELEMENT_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.WINDOW_FUNCTIONS][0].config.windows.elementSpec;

const FIND_AND_REPLACE_OPTIONS_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.FIND_AND_REPLACE][0].config.findAndReplaceOptions;

const AGGREGATE_TAG_KEY_DISPLAY_NAME_EXCEPTIONS: Record<string, string> = {
  STRING_AGG: 'Text concat',
};

/** @package */
export const AGGREGATE_TAG_KEY_DISPLAY_NAMES: Record<
  GetTaggedUnionTagValues<typeof AGGREGATION_ELEMENT_SHAPE>,
  string
> = objectFromEntries(
  AGGREGATION_ELEMENT_SHAPE.choices
    .map((choice) => choice.fields[AGGREGATION_ELEMENT_SHAPE.tagKey].value)
    .map((tagValue) => [
      tagValue,
      AGGREGATE_TAG_KEY_DISPLAY_NAME_EXCEPTIONS[tagValue] ?? capitalize(constantCaseToWords(tagValue)),
    ])
);

/** @package */
export const WINDOW_TAG_KEY_DISPLAY_NAMES: Record<
  GetTaggedUnionTagValues<typeof WINDOW_ELEMENT_SHAPE>,
  string
> = objectFromEntries(
  WINDOW_ELEMENT_SHAPE.choices
    .map((choice) => choice.fields[WINDOW_ELEMENT_SHAPE.tagKey].value)
    .map((tagValue) => [tagValue, capitalize(constantCaseToWords(tagValue))])
);

const AGGREGATE_OR_WINDOW_TAG_KEY_DISPLAY_NAMES = {
  ...AGGREGATE_TAG_KEY_DISPLAY_NAMES,
  ...WINDOW_TAG_KEY_DISPLAY_NAMES,
};

const ORDER_BY_COLUMNS_ELEMENT_UI_SCHEMA: ShapeUISchema = {
  [NUM_COLUMNS_KEY]: 2,
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'direction'],
  column: {
    displayName: 'Column',
    configShapePath: ['column'],
  },
  direction: {
    displayName: 'Direction',
    configShapePath: ['direction'],
    fieldSettings: {
      placeholder: 'Select direction',
      defaultValue: 'ASCENDING',
      displayNames: {
        ASCENDING: 'Ascending',
        DESCENDING: 'Descending',
      },
    },
  },
};

const FUNCTIONS_WITHOUT_COLUMN = new Set([
  'COUNT',
  'CUME_DIST',
  'DENSE_RANK',
  'PERCENT_RANK',
  'RANK',
  'ROW_NUMBER',
]);

/** @package */
export function onChangeAggregationOrWindowMiddleware<
  T extends
    | PipelineJsonSpecToTsType<typeof AGGREGATION_ELEMENT_SHAPE, 'draft'>
    | PipelineJsonSpecToTsType<typeof WINDOW_ELEMENT_SHAPE, 'draft'>,
>(
  newConfig: T,
  oldConfig: T,
  dataFramesInfos: ConfigDataFramesInfos<
    PipelineStepType.AGGREGATE_DATASET | PipelineStepType.WINDOW_FUNCTIONS,
    0
  >
): T {
  if (newConfig?.aggregationFunction == null) {
    return newConfig;
  }
  if (FUNCTIONS_WITHOUT_COLUMN.has(newConfig.aggregationFunction)) {
    return {
      ...newConfig,
      newColumnName:
        newConfig.newColumnName ?? AGGREGATE_OR_WINDOW_TAG_KEY_DISPLAY_NAMES[newConfig.aggregationFunction],
    };
  } else if (
    // If aggregation function and column are set, _and_ one of those is being changed, then
    // set the new column name to <Aggregation fn>(<Column name>)
    // eslint-disable-next-line benchlint/no-in-operator
    'column' in newConfig &&
    newConfig.column != null &&
    (oldConfig?.aggregationFunction !== newConfig.aggregationFunction ||
      oldConfig?.column !== newConfig.column)
  ) {
    const newColumnName = `${AGGREGATE_OR_WINDOW_TAG_KEY_DISPLAY_NAMES[newConfig.aggregationFunction]} of ${
      findAndAssertColumn(dataFramesInfos['Input dataset'][0].dataFrameSpec, newConfig.column).displayName
    }`;
    // If the aggregation function is STRING_AGG, also set the default separator to an empty string
    return newConfig.aggregationFunction === 'STRING_AGG'
      ? {...newConfig, newColumnName, separator: newConfig.separator ?? ''}
      : {...newConfig, newColumnName};
  }
  return newConfig;
}

/** @package */
export const FIND_AND_REPLACE_TAG_KEY_DISPLAY_NAMES: Record<
  GetTaggedUnionTagValues<typeof FIND_AND_REPLACE_OPTIONS_SHAPE>,
  string
> = objectFromEntries([
  ['LITERAL', capitalize('Text')],
  ['REGEX', `${capitalize('Regular expressions')} (RE2 syntax)`], // Leave RE2 out so its not uncapitalized
]);

/** @package */
export function onChangeFindAndReplaceMiddleware<
  T extends PipelineJsonSpecToTsType<typeof FIND_AND_REPLACE_OPTIONS_SHAPE, 'draft'>,
>(newConfig: T, oldConfig: T): T {
  if (newConfig?.findAndReplaceType == null) {
    return newConfig;
  }
  const sharedOptions = {
    matchAllOccurrences: false,
    replacementString: '',
  };
  if (newConfig?.findAndReplaceType === 'LITERAL') {
    return {
      literalString: '',
      matchWholeWord: true,
      ...sharedOptions,
      ...newConfig,
    };
  } else {
    return {
      patternString: '',
      ...sharedOptions,
      ...newConfig,
    };
  }
}

export const AGGREGATE_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.AGGREGATE_DATASET, 0> =
  {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['aggregations', 'groupColumns'],
    groupColumns: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['groupColumns'],
      groupColumns: {
        displayName: 'Group by',
        configShapePath: ['groupColumns'],
      },
    },
    aggregations: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['aggregations'],
      aggregations: {
        displayName: 'Aggregate',
        configShapePath: ['aggregations'],
        elementUISchema: {
          configShapePath: [],
          fieldSettings: {
            tagKeyDisplayNames: AGGREGATE_TAG_KEY_DISPLAY_NAMES,
            tagKeyDisplayName: 'Aggregation function',
            placeholder: 'Select function',
            choiceFieldOrder: ['column', 'newColumnName', 'separator', 'orderByColumns'],
            [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: (newValue, _path, oldConfig, dataFramesInfos) =>
              onChangeAggregationOrWindowMiddleware(
                newValue as PipelineJsonSpecToTsType<typeof AGGREGATION_ELEMENT_SHAPE, 'draft'>,
                oldConfig as PipelineJsonSpecToTsType<typeof AGGREGATION_ELEMENT_SHAPE, 'draft'>,
                dataFramesInfos as ConfigDataFramesInfos<PipelineStepType.AGGREGATE_DATASET, 0>
              ),
            choiceUISchema: {
              column: {displayName: 'Column to aggregate', configShapePath: ['column']},
              newColumnName: {displayName: 'New column name', configShapePath: ['newColumnName']},
              separator: {displayName: 'Separator', configShapePath: ['separator']},
              orderByColumns: {
                displayName: 'Order by',
                configShapePath: ['orderByColumns'],
                elementUISchema: ORDER_BY_COLUMNS_ELEMENT_UI_SCHEMA,
              },
            },
          },
        },
      },
    },
  };

export const APPLY_COLUMN_MAPPING_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.APPLY_COLUMN_MAPPING,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['outputColumns', 'keepUnmappedColumns'],
  outputColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['outputColumns'],
    outputColumns: {
      [CUSTOM_COMPONENT_KEY]: ApplyColumnMappingEditor,
    },
  },
  keepUnmappedColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['keepUnmappedColumns'],
    keepUnmappedColumns: {
      displayName: 'Extra columns',
      configShapePath: ['keepUnmappedColumns'],
      editorOverride: StepConfigEditorOverride.CHECKBOX,
      fieldSettings: {
        checkboxLabel: 'Keep input columns that do not match any column defined above',
      },
    },
  },
};

export const BIN_DATA_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.BIN_DATA, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'numBins'],
  column: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['column'],
    column: {displayName: 'Column', configShapePath: ['column']},
  },
  numBins: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['numBins'],
    numBins: {displayName: 'Number of bins', configShapePath: ['numBins']},
  },
};

export const CONVERT_ASM_TO_CSV_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CONVERT_ASM_TO_CSV,
  0
> = {
  [BACKEND_FIELD_GENERATOR_CONFIG]: {triggerFields: []},
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['transformType'],
  transformType: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['transformType'],
    transformType: {
      displayName: 'Transform Type',
      configShapePath: ['transformType'],
      fieldSettings: {placeholder: 'Select transform type'},
    },
  },
};

export const CONVERT_FILE_TO_DATASET_WITH_DATA_ENTRY_ASSISTANT_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CONVERT_FILE_TO_DATASET_WITH_DATA_ENTRY_ASSISTANT,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['datasetName', 'instructions', 'columns'],
  [REQUIRES_MANUAL_PREVIEW]: true,
  [BANNER_KEY]: {
    text: 'This feature is powered by AI, and may make mistakes. Always verify the accuracy of AI-generated content.',
    supportingInfo:
      'To generate the requested content, the AI system will analyze the content of the input file. For more information on AI security and privacy, [look here](https://help.benchling.com/hc/en-us/articles/20901323667853-Security-and-privacy-for-LLMs-and-AI-powered-features).',
    bannerType: InlineBannerType.ATTENTION,
    placement: 'below',
  },
  datasetName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['datasetName'],
    datasetName: {
      displayName: 'Dataset Name',
      configShapePath: ['datasetName'],
    },
  },
  instructions: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['instructions'],
    instructions: {
      displayName: 'Add instructions',
      infoText:
        'Include any additional instructions or clarifications to help the AI system understand how to create the dataset',
      configShapePath: ['instructions'],
    },
  },
  columns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['columns'],
    columns: {
      displayName: 'Columns',
      infoText:
        'The column names and types to use for generation. If empty, the step will use AI to generate suggested columns.',
      configShapePath: ['columns'],
      [BANNER_KEY]: {
        text: 'Columns will be automatically generated by AI if not specified',
        supportingInfo: 'Use the + above to add a list of specific columns',
        bannerType: InlineBannerType.ATTENTION,
        placement: 'below',
        initialShowMore: true,
      },
      elementUISchema: {
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['name', 'data_type'],
        name: {
          displayName: 'Name',
          configShapePath: ['name'],
        },
        data_type: {
          displayName: 'Data Type',
          configShapePath: ['data_type'],
        },
      },
    },
  },
};

export const CONVERT_JSON_TO_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CONVERT_JSON_TO_DATASET,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['name', 'columns'],
  name: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['name'],
    name: {
      displayName: 'Dataset Name',
      configShapePath: ['name'],
    },
  },
  columns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['columns'],
    columns: {
      displayName: 'Columns',
      configShapePath: ['columns'],
      elementUISchema: {
        [NUM_COLUMNS_KEY]: 2,
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['name', 'path', 'include', 'required'],
        name: {
          displayName: 'Column Name',
          infoText: "May include another column value in name via '$' substitution e.g.: 'ColName $unit$'.",
          configShapePath: ['name'],
        },
        path: {
          displayName: 'JSON Path to Column Value',
          infoText: "Full path to the JSON leaf value to use for the column, e.g.: 'path/to/leaf'.",
          configShapePath: ['path'],
        },
        include: {
          displayName: 'Include in dataset?',
          infoText: "May set to 'No' if using as part of another column name.",
          configShapePath: ['include'],
        },
        required: {
          displayName: 'Required?',
          infoText: 'Dataset creation will fail if the JSON path is missing from the input.',
          configShapePath: ['required'],
        },
      },
    },
  },
};

export const CREATE_METERING_USAGE_CHART_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CREATE_METERING_USAGE_CHART,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['dateRange', 'productType', 'userId'],
  [NUM_COLUMNS_KEY]: 3,
  dateRange: {
    displayName: 'Date range',
    configShapePath: ['dateRange'],
    editorOverride: StepConfigEditorOverride.DATE_RANGE,
    fieldSettings: {
      startKey: 'startDate',
      endKey: 'endDate',
    },
  },
  productType: {
    displayName: 'Product type',
    configShapePath: ['productType'],
    fieldSettings: {
      displayNames: {
        AI_ASK: 'AI Ask',
        COMPOSE: 'Compose',
        DATA_ENTRY_AGENT: 'Data Entry Agent',
        DEEP_RESEARCH: 'Deep Research',
        STRUCTURE_PREDICTION: 'Structure Prediction',
      },
    },
  },
  userId: {
    displayName: 'User ID',
    configShapePath: ['userId'],
  },
};

export const CREATE_PROCEDURE_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CREATE_PROCEDURE_DATASET,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataSourceType', 'dataSource', 'seedItems'],
  dataSourceType: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataSourceType'],
    dataSourceType: {
      [CUSTOM_COMPONENT_KEY]: DataSourceTypeEditor,
    },
  },
  dataSource: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataSource'],
    dataSource: {
      [CUSTOM_COMPONENT_KEY]: DataSourceEditor,
    },
  },
  seedItems: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['seedItems'],
    seedItems: {
      [CUSTOM_COMPONENT_KEY]: SeedItemsEditor,
    },
  },
};

export const EXTRACT_DATA_FRAME_VALUE_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.EXTRACT_DATA_FRAME_VALUE,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['columnSystemName', 'rowIndex'],
  columnSystemName: {
    displayName: 'Column',
    configShapePath: ['columnSystemName'],
  },
  rowIndex: {
    displayName: 'Row Index',
    infoText: 'Index of the row to extract value from',
    configShapePath: ['rowIndex'],
  },
};

export const FORMAT_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.FORMAT_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['formatColumns'],
  formatColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['formatColumns'],
    formatColumns: {
      displayName: 'Column formats',
      configShapePath: ['formatColumns'],
      elementUISchema: {
        [NUM_COLUMNS_KEY]: 2,
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'format'],
        column: {
          displayName: 'Column',
          configShapePath: ['column'],
        },
        format: {
          configShapePath: ['format'],
          fieldSettings: {
            choiceFieldOrder: ['precision'],
            choiceUISchema: {
              precision: {
                displayName: 'Decimal places',
                configShapePath: ['precision'],
                infoText: 'Number of decimal places to display (0-20)',
              },
            },
          },
        },
      },
    },
  },
};

export const MAP_JSON_TO_DATASETS_AND_FILES_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.MAP_JSON_TO_DATASETS_AND_FILES,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['datasets', 'transforms'],
  datasets: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['datasets'],
    datasets: {
      displayName: 'Datasets',
      configShapePath: ['datasets'],
      elementUISchema: {
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['name', 'columns', 'include', 'is_metadata'],
        name: {
          displayName: 'Dataset Name',
          configShapePath: ['name'],
        },
        columns: {
          displayName: 'Columns',
          configShapePath: ['columns'],
          elementUISchema: {
            [NUM_COLUMNS_KEY]: 2,
            [FIELD_ORDER_UI_SCHEMA_KEY]: ['name', 'path', 'include', 'required'],
            name: {
              displayName: 'Column Name',
              infoText:
                "May include another column value in name via '$' substitution e.g.: 'ColName $unit$'.",
              configShapePath: ['name'],
            },
            path: {
              displayName: 'JSON Path to Column Value',
              infoText: "Full path to the JSON leaf value to use for the column, e.g.: 'path/to/leaf'.",
              configShapePath: ['path'],
            },
            include: {
              displayName: 'Include in dataset?',
              infoText: "May set to 'No' if using as part of another column name.",
              configShapePath: ['include'],
            },
            required: {
              displayName: 'Required?',
              infoText: 'Dataset creation will fail if the JSON path is missing from the input.',
              configShapePath: ['required'],
            },
          },
        },
        include: {
          displayName: 'Include in output?',
          infoText: "May set to 'No' if adding to another dataset via a join transform.",
          configShapePath: ['include'],
        },
        is_metadata: {
          displayName: 'Output as metadata JSON file?',
          infoText:
            'If set, dataset will be output as a file containing a JSON blob. Must have a single value per column.',
          configShapePath: ['is_metadata'],
        },
      },
    },
  },
  transforms: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['transforms'],
    transforms: {
      displayName: 'Transforms',
      configShapePath: ['transforms'],
      elementUISchema: {
        displayName: 'Transform',
        configShapePath: [],
        fieldSettings: {
          placeholder: 'Select a transform',
          tagKeyDisplayName: 'Transform Type',
          choiceFieldOrder: [
            'dataset_1',
            'dataset_2',
            'join_key_1',
            'join_key_2',
            'dataset',
            'path',
            'value_path',
            'label_path',
          ],
          choiceUISchema: {
            dataset_1: {
              displayName: 'Dataset 1',
              configShapePath: ['dataset_1'],
            },
            dataset_2: {
              displayName: 'Dataset 2',
              configShapePath: ['dataset_2'],
            },
            join_key_1: {
              displayName: 'Dataset 1 Join Column',
              configShapePath: ['join_key_1'],
            },
            join_key_2: {
              displayName: 'Dataset 2 Join Column',
              configShapePath: ['join_key_2'],
            },
            dataset: {
              displayName: 'Dataset',
              configShapePath: ['dataset'],
            },
            path: {
              displayName: 'JSON Path to list to pivot on',
              configShapePath: ['path'],
            },
            value_path: {
              displayName: 'JSON Path to leaf value to use for pivot column value',
              configShapePath: ['value_path'],
            },
            label_path: {
              displayName: 'JSON Path to leaf value to use for pivot column name',
              configShapePath: ['label_path'],
            },
          },
        },
      },
    },
  },
};

export const CONVERT_TO_ASM_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.CONVERT_TO_ASM, 0> = {
  [BACKEND_FIELD_GENERATOR_CONFIG]: {triggerFields: ['asmVendor']},
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['asmVendor', 'transformType'],
  asmVendor: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['asmVendor'],
    asmVendor: {
      displayName: 'Instrument Vendor',
      infoText: 'If not set, the vendor will be auto-detected from the file contents.',
      configShapePath: ['asmVendor'],
      fieldSettings: {placeholder: 'Optionally select vendor'},
    },
  },
  transformType: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['transformType'],
    transformType: {
      displayName: 'CSV Transform Type',
      infoText: 'If specified, output file will be transformed CSV.',
      configShapePath: ['transformType'],
      fieldSettings: {placeholder: 'Optionally select CSV transform type'},
    },
  },
};

export const CREATE_DATASET_FROM_FILE_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CREATE_DATASET_FROM_FILE,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'datasetName',
    'csvDelimiter',
    'customCsvDelimiter',
    'dataLocale',
    'sheetName',
  ],
  datasetName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['datasetName'],
    datasetName: {
      displayName: 'Dataset name',
      configShapePath: ['datasetName'],
    },
  },
  csvDelimiter: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['csvDelimiter'],
    csvDelimiter: {
      displayName: 'CSV delimiter',
      configShapePath: ['csvDelimiter'],
      fieldSettings: {
        defaultValue: ',',
      },
    },
  },
  customCsvDelimiter: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['customCsvDelimiter'],
    customCsvDelimiter: {
      displayName: 'Custom CSV delimiter',
      infoText: "Only used if 'Custom' is selected for CSV delimiter.",
      configShapePath: ['customCsvDelimiter'],
    },
  },
  dataLocale: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataLocale'],
    dataLocale: {
      displayName: 'Data locale',
      configShapePath: ['dataLocale'],
      fieldSettings: {
        defaultValue: 'en-US',
      },
    },
  },
  sheetName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['sheetName'],
    sheetName: {
      displayName: 'Spreadsheet sheet name',
      infoText: 'Used to specify the sheet/tab to use for multi-sheet Excel files.',
      configShapePath: ['sheetName'],
    },
  },
};

export const EXTRACT_OR_REMOVE_LINES_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.EXTRACT_OR_REMOVE_LINES,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['actionType', 'startLine', 'endLine'],
  actionType: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['actionType'],
    actionType: {
      displayName: 'Action type',
      configShapePath: ['actionType'],
      fieldSettings: {
        defaultValue: 'EXTRACT_LINES',
      },
    },
  },
  startLine: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['startLine'],
    startLine: {displayName: 'Start line', configShapePath: ['startLine']},
  },
  endLine: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['endLine'],
    endLine: {displayName: 'End line', configShapePath: ['endLine']},
  },
};

export const onChangeComputedColumnConfigMiddleware = (
  newValue: JSONValue,
  path: ConfigShapePath,
  oldConfig: JSONValue,
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>
): JSONValue => {
  const config = oldConfig as Record<string, JSONValue>;
  // Only apply when replaceColumn is changing
  if (path.length !== 1 || path[0] !== 'replaceColumn') {
    return config;
  }
  if (newValue == null || newValue === config.replaceColumn) {
    return config;
  }
  const selectedColumn = dataFramesInfos['Input dataset']?.[0]?.dataFrameSpec.columns.find(
    (column) => column.systemName === newValue
  );
  if (selectedColumn?.displayName == null) {
    return config;
  }
  return {...config, replaceColumn: newValue, newColumnName: selectedColumn.displayName};
};

export const COMPUTED_COLUMN_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.COMPUTED_COLUMN, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['newColumnName', 'replaceColumn', 'formula'],
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeComputedColumnConfigMiddleware,
  newColumnName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['newColumnName'],
    newColumnName: {displayName: 'New column name', configShapePath: ['newColumnName']},
  },
  replaceColumn: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['replaceColumn'],
    replaceColumn: {
      displayName: 'Replace column (optional)',
      infoText:
        'If specified, this column will be replaced in-place. If left blank, a new column will be added.',
      configShapePath: ['replaceColumn'],
      fieldSettings: {allowSelectNull: true},
    },
  },
  formula: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['formula'],
    formula: {
      displayName: 'Formula text',
      [CUSTOM_COMPONENT_KEY]: AnalysisFormulaEditor,
    },
  },
};

const PIVOT_ELEMENT_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.PIVOT_DATASET][0].config.valueColumns.elementSpec;

/** @package */
export const onChangePivotElementMiddleware: OnChangeConfigMiddlewareFn<
  typeof PIVOT_ELEMENT_SHAPE,
  PipelineStepType.PIVOT_DATASET,
  0
> = (newConfig, oldConfig, dataFramesInfos) => {
  if (newConfig?.aggregationFunction == null) {
    return newConfig;
  }
  if (
    // If newColumnName is not set, and a column is set, and one of aggregation functions or columns is being
    // changed, then set the new column name to <Aggregation fn>(<Column name>)
    newConfig.newColumnName == null &&
    newConfig.column != null &&
    (oldConfig?.aggregationFunction !== newConfig.aggregationFunction ||
      oldConfig?.column !== newConfig.column)
  ) {
    return {
      ...newConfig,
      newColumnName: `${AGGREGATE_TAG_KEY_DISPLAY_NAMES[newConfig.aggregationFunction]} of ${
        findAndAssertColumn(dataFramesInfos['Input dataset'][0].dataFrameSpec, newConfig.column).displayName
      }`,
    };
  }
  return newConfig;
};

export const PIVOT_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.PIVOT_DATASET, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['pivotColumns', 'valueColumns', 'groupColumns'],
  pivotColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['pivotColumns'],
    pivotColumns: {
      displayName: 'Pivot columns',
      configShapePath: ['pivotColumns'],
    },
  },
  valueColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['valueColumns'],
    valueColumns: {
      displayName: 'Values',
      configShapePath: ['valueColumns'],
      elementUISchema: {
        configShapePath: [],
        fieldSettings: {
          tagKeyDisplayNames: AGGREGATE_TAG_KEY_DISPLAY_NAMES,
          tagKeyDisplayName: 'Aggregation function',
          placeholder: 'Select function',
          choiceFieldOrder: ['column', 'newColumnName'],
          [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: (newValue, _path, oldConfig, dataFramesInfos) =>
            onChangePivotElementMiddleware(
              newValue as PipelineJsonSpecToTsType<typeof PIVOT_ELEMENT_SHAPE, 'draft'>,
              oldConfig as PipelineJsonSpecToTsType<typeof PIVOT_ELEMENT_SHAPE, 'draft'>,
              dataFramesInfos as ConfigDataFramesInfos<PipelineStepType.PIVOT_DATASET, 0>
            ),
          choiceUISchema: {
            column: {displayName: 'Column to aggregate', configShapePath: ['column']},
            newColumnName: {displayName: 'New column name', configShapePath: ['newColumnName']},
          },
        },
      },
    },
  },
  groupColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['groupColumns'],
    groupColumns: {
      displayName: 'Pivot rows',
      configShapePath: ['groupColumns'],
    },
  },
};

export const RUN_HIGHRES_PROTOCOL_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.RUN_HIGHRES_PROTOCOL,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'scientificProtocolId',
    'name',
    'requestedByUserId',
    'cellarioSystemId',
    'ports',
  ],
  [BACKEND_FIELD_GENERATOR_CONFIG]: {triggerFields: ['scientificProtocolId']},
  scientificProtocolId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['scientificProtocolId'],
    scientificProtocolId: {
      displayName: 'Scientific Protocol',
      infoText:
        'The HighRes scientific protocol to execute. This determines the available ports and Cellario systems.',
      configShapePath: ['scientificProtocolId'],
    },
  },
  name: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['name'],
    name: {
      displayName: 'Name',
      infoText: 'A name for the work order that will be created in HighRes.',
      configShapePath: ['name'],
    },
  },
  requestedByUserId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['requestedByUserId'],
    requestedByUserId: {
      displayName: 'Requested by',
      infoText: 'The HighRes user who is requesting this work order.',
      configShapePath: ['requestedByUserId'],
    },
  },
  cellarioSystemId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['cellarioSystemId'],
    cellarioSystemId: {
      displayName: 'Cellario System',
      infoText:
        'The Cellario system that will execute the protocol. Available systems depend on the selected scientific protocol.',
      configShapePath: ['cellarioSystemId'],
    },
  },
  ports: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['ports'],
    ports: {
      displayName: 'Ports',
      infoText:
        'Configure the input/output ports for the protocol. Each port maps plate barcodes to a labware type.',
      configShapePath: ['ports'],
      elementUISchema: {
        [NUM_COLUMNS_KEY]: 2,
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['portId', 'labwareTypeId', 'plateIdColumn'],
        portId: {
          displayName: 'Port',
          infoText: 'The protocol port. Available ports depend on the selected scientific protocol.',
          configShapePath: ['portId'],
        },
        labwareTypeId: {
          displayName: 'Labware Type',
          infoText: 'The type of labware (plate format) used for this port.',
          configShapePath: ['labwareTypeId'],
        },
        plateIdColumn: {
          displayName: 'Plate Column',
          infoText: 'The column in the input dataset that contains the plate barcodes for this port.',
          configShapePath: ['plateIdColumn'],
        },
      },
    },
  },
};

export const SEND_USER_NOTIFICATION_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.SEND_USER_NOTIFICATION,
  0
> = {
  // In V2, fields not in FIELD_ORDER are automatically hidden.
  // notificationType is a required enum but an internal implementation detail not shown to users.
  // A defaultValue is needed so makeInitialConfigState produces a valid config.
  [FIELD_ORDER_UI_SCHEMA_KEY]: [],
  notificationType: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['notificationType'],
    notificationType: {
      displayName: 'Notification type',
      configShapePath: ['notificationType'],
      fieldSettings: {
        defaultValue: 'NDC_INGESTED',
      },
    },
  },
};

export const SORT_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.SORT_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['sortOptions'],
  sortOptions: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['sortOptions'],
    sortOptions: {
      displayName: 'Sort by',
      configShapePath: ['sortOptions'],
      elementUISchema: {
        [NUM_COLUMNS_KEY]: 2,
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'direction'],
        column: {
          displayName: 'Column',
          configShapePath: ['column'],
        },
        direction: {
          displayName: 'Direction',
          configShapePath: ['direction'],
          fieldSettings: {
            defaultValue: 'ASCENDING',
            displayNames: {
              ASCENDING: 'Ascending',
              DESCENDING: 'Descending',
            },
          },
        },
      },
    },
  },
};

export const UNPIVOT_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.UNPIVOT_DATASET, 0> = {
  // In V2, excludedColumns is automatically hidden by omitting from FIELD_ORDER.
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['unpivotColumns', 'nameColumn', 'valueColumn'],
  unpivotColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['unpivotColumns'],
    unpivotColumns: {displayName: 'Unpivot columns', configShapePath: ['unpivotColumns']},
  },
  nameColumn: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['nameColumn'],
    nameColumn: {displayName: 'Name column', configShapePath: ['nameColumn']},
  },
  valueColumn: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['valueColumn'],
    valueColumn: {displayName: 'Value column', configShapePath: ['valueColumn']},
  },
};

export const WAREHOUSE_SQL_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.WAREHOUSE_SQL_DATASET,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName', 'parameterizedSQLQuery'],
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Table name',
      configShapePath: ['dataFrameName'],
    },
  },
  parameterizedSQLQuery: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['parameterizedSQLQuery'],
    parameterizedSQLQuery: {
      displayName: 'SQL query',
      infoText: 'Write a SQL query against the data warehouse to create a table.',
      [CUSTOM_COMPONENT_KEY]: (props) => (
        <UserDefinedFunctionEditor {...props} fieldName="parameterizedSQLQuery" mode="sql" />
      ),
    },
  },
};

export const WINDOW_FUNCTIONS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.WINDOW_FUNCTIONS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['windows', 'orderByColumns'],
  orderByColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['orderByColumns'],
    orderByColumns: {
      displayName: 'Order by',
      configShapePath: ['orderByColumns'],
      elementUISchema: ORDER_BY_COLUMNS_ELEMENT_UI_SCHEMA,
    },
  },
  windows: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['windows'],
    windows: {
      displayName: 'Windows',
      configShapePath: ['windows'],
      elementUISchema: {
        configShapePath: [],
        fieldSettings: {
          tagKeyDisplayNames: WINDOW_TAG_KEY_DISPLAY_NAMES,
          tagKeyDisplayName: 'Window function',
          placeholder: 'Select function',
          choiceFieldOrder: ['column', 'newColumnName', 'partitionColumns', 'orderByColumns'],
          [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: (newValue, _path, oldConfig, dataFramesInfos) =>
            onChangeAggregationOrWindowMiddleware(
              newValue as PipelineJsonSpecToTsType<typeof WINDOW_ELEMENT_SHAPE, 'draft'>,
              oldConfig as PipelineJsonSpecToTsType<typeof WINDOW_ELEMENT_SHAPE, 'draft'>,
              dataFramesInfos as ConfigDataFramesInfos<PipelineStepType.WINDOW_FUNCTIONS, 0>
            ),
          choiceUISchema: {
            column: {displayName: 'Column to aggregate', configShapePath: ['column']},
            newColumnName: {displayName: 'New column name', configShapePath: ['newColumnName']},
            partitionColumns: {displayName: 'Partition by', configShapePath: ['partitionColumns']},
            orderByColumns: {
              displayName: 'Order by',
              configShapePath: ['orderByColumns'],
              elementUISchema: ORDER_BY_COLUMNS_ELEMENT_UI_SCHEMA,
            },
          },
        },
      },
    },
  },
};

export const FIND_AND_REPLACE_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.FIND_AND_REPLACE, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['selectedColumns', 'findAndReplaceOptions'],
  selectedColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['selectedColumns'],
    selectedColumns: {
      displayName: 'Columns',
      infoText:
        'Specify which text columns to find and replace values in. If no columns are selected, all text columns are included.',
      configShapePath: ['selectedColumns'],
    },
  },
  findAndReplaceOptions: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['findAndReplaceOptions'],
    findAndReplaceOptions: {
      displayName: 'Options',
      configShapePath: ['findAndReplaceOptions'],
      fieldSettings: {
        tagKeyDisplayNames: FIND_AND_REPLACE_TAG_KEY_DISPLAY_NAMES,
        tagKeyDisplayName: 'Search with text or regular expressions?',
        choiceFieldOrder: [
          'literalString',
          'patternString',
          'replacementString',
          'matchWholeWord',
          'matchAllOccurrences',
        ],
        [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: (newValue, _path, _oldConfig) =>
          onChangeFindAndReplaceMiddleware(
            newValue as PipelineJsonSpecToTsType<typeof FIND_AND_REPLACE_OPTIONS_SHAPE, 'draft'>,
            _oldConfig as PipelineJsonSpecToTsType<typeof FIND_AND_REPLACE_OPTIONS_SHAPE, 'draft'>
          ),
        choiceUISchema: {
          literalString: {displayName: 'Search text', configShapePath: ['literalString']},
          patternString: {displayName: 'Regex pattern', configShapePath: ['patternString']},
          matchWholeWord: {displayName: 'Match whole word?', configShapePath: ['matchWholeWord']},
          matchAllOccurrences: {
            displayName: 'Match all occurrences within a cell?',
            configShapePath: ['matchAllOccurrences'],
          },
          replacementString: {displayName: 'Replacement string', configShapePath: ['replacementString']},
        },
      },
    },
  },
};

export const HIDE_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.HIDE_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['columns', 'columnsToKeep'],
  columns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['columns'],
    columns: {
      displayName: 'Columns to hide',
      configShapePath: ['columns'],
    },
  },
  columnsToKeep: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['columnsToKeep'],
    columnsToKeep: {
      displayName: 'Columns to keep',
      configShapePath: ['columnsToKeep'],
    },
  },
};

export const RETYPE_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.RETYPE_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['retypeColumns'],
  retypeColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['retypeColumns'],
    retypeColumns: {
      displayName: 'Column formats',
      configShapePath: ['retypeColumns'],
      elementUISchema: {
        [NUM_COLUMNS_KEY]: 2,
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'type'],
        column: {
          displayName: 'Column',
          configShapePath: ['column'],
        },
        type: {
          displayName: 'Data format',
          configShapePath: ['type'],
          fieldSettings: {
            defaultValue: 'INT',
            displayNames: TYPES_TO_DISPLAY_NAMES,
          },
        },
      },
    },
  },
};

export const RENAME_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.RENAME_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['renameColumns'],
  renameColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['renameColumns'],
    renameColumns: {
      displayName: 'Columns',
      configShapePath: ['renameColumns'],
      elementUISchema: {
        [NUM_COLUMNS_KEY]: 2,
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'name'],
        column: {
          displayName: 'Column',
          configShapePath: ['column'],
        },
        name: {
          displayName: 'New name',
          configShapePath: ['name'],
        },
      },
    },
  },
};

export const REORDER_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.REORDER_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['reorderColumns'],
  reorderColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['reorderColumns'],
    reorderColumns: {
      displayName: 'Columns',
      [CUSTOM_COMPONENT_KEY]: ColumnDragAndDrop,
    },
  },
};

export const REGRESSION_INTERPOLATION_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.REGRESSION_INTERPOLATION,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['inputColumn', 'isInputX', 'inputPartitionColumn'],
  inputColumn: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['inputColumn'],
    inputColumn: {displayName: 'Input column', configShapePath: ['inputColumn']},
  },
  isInputX: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['isInputX'],
    isInputX: {
      displayName: 'Select prediction mode',
      infoText:
        'Whether the input column values will be treated as values of X or values of Y when calculating the interpolated values.',
      [CUSTOM_COMPONENT_KEY]: IsInputXToggleGroup,
    },
  },
  inputPartitionColumn: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['inputPartitionColumn'],
    inputPartitionColumn: {
      displayName: 'Input series column (optional)',
      infoText:
        "If specified, then the parameter table (regression model) and the input table will be joined based on this column's values.",
      configShapePath: ['inputPartitionColumn'],
      fieldSettings: {allowSelectNull: true},
    },
  },
};

export {onChangeColorPaletteMiddleware, getDefaultSeriesColorConfig};

export const CHART_VIEW_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.CHART_VIEW, 0> = {
  [TAB_ORDER_UI_SCHEMA_KEY]: ['configure', 'style'],

  configure: {
    displayName: 'Configure',
    uiSchema: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: [
        'boxPlotConfigure',
        'pieChartConfigure',
        'lineChartConfigure',
        'heatMapConfigure',
        'scatterChartConfigure',
        'barChartConfigure',
      ],
      boxPlotConfigure: createChartTypeSchema('BOX_PLOT', BOX_PLOT_CONFIG_SECTIONS),
      pieChartConfigure: createChartTypeSchema('PIE_CHART', PIE_CHART_CONFIG_SECTIONS),
      lineChartConfigure: createChartTypeSchema('LINE_CHART', LINE_CHART_CONFIG_SECTIONS),
      heatMapConfigure: createChartTypeSchema('HEATMAP', HEATMAP_CONFIG_SECTIONS),
      scatterChartConfigure: createChartTypeSchema('SCATTER_PLOT', SCATTER_CHART_CONFIG_SECTIONS),
      barChartConfigure: createChartTypeSchema('BAR_CHART', BAR_CHART_CONFIG_SECTIONS),
    },
  },
  style: {
    displayName: 'Style',
    uiSchema: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: [
        'boxPlotStyle',
        'pieChartStyle',
        'lineChartStyle',
        'heatmapStyle',
        'scatterChartStyle',
        'barChartStyle',
      ],
      boxPlotStyle: createChartTypeSchema('BOX_PLOT', BOX_PLOT_STYLE_SECTIONS),
      pieChartStyle: createChartTypeSchema('PIE_CHART', PIE_CHART_STYLE_SECTIONS),
      lineChartStyle: createChartTypeSchema('LINE_CHART', LINE_CHART_STYLE_SECTIONS),
      heatmapStyle: createChartTypeSchema('HEATMAP', HEATMAP_STYLE_SECTIONS),
      scatterChartStyle: createChartTypeSchema('SCATTER_PLOT', SCATTER_CHART_STYLE_SECTIONS),
      barChartStyle: createChartTypeSchema('BAR_CHART', BAR_CHART_STYLE_SECTIONS),
    },
  },
};

export const FILTER_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.FILTER_DATASET, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['filter'],
  filter: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['filter'],
    filter: {
      displayName: 'Column filters',
      [CUSTOM_COMPONENT_KEY]: FilterStepFilterConfig,
    },
  },
};

export const SEND_TO_CONNECTION_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.SEND_TO_CONNECTION,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionId'],
  connectionId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionId'],
    connectionId: {
      displayName: 'Connection',
      [CUSTOM_COMPONENT_KEY]: ({config: fullConfig, onChangeConfig, disabled}) => (
        <SearchEditor
          config={fullConfig.connectionId as string | null}
          onChangeConfig={(newValue) => {
            onChangeConfig(fullConfig, ['connectionId'], newValue);
          }}
          modelTypes={[ModelTypeConstants.CONNECTION]}
          disabled={disabled}
        />
      ),
    },
  },
};

export const USER_DEFINED_FUNCTION_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.USER_DEFINED_FUNCTION,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['userDefinedCode', 'nameSection'],
  userDefinedCode: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['userDefinedCode'],
    userDefinedCode: {
      displayName: 'Code',
      [CUSTOM_COMPONENT_KEY]: (props) => <UserDefinedFunctionEditor {...props} fieldName="userDefinedCode" />,
    },
  },
  nameSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['name'],
    name: {
      displayName: 'Name',
      configShapePath: ['name'],
    },
  },
};

export const DATA_CATALOG_FILE_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.DATA_CATALOG_FILE, 0> =
  {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['fileId', 'fileName'],
    fileId: {
      displayName: 'File',
      configShapePath: ['fileId'],
    },
    fileName: {
      displayName: 'File name',
      configShapePath: ['fileName'],
    },
  };

export const DATA_CATALOG_FILES_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.DATA_CATALOG_FILES,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['fileIds', 'fileBaseName'],
  fileIds: {
    displayName: 'Files',
    configShapePath: ['fileIds'],
  },
  fileBaseName: {
    displayName: 'Base file name',
    infoText:
      'When multiple files are selected, this will be prepended to the original file name. If only one file is selected, this will be the name of the file.',
    configShapePath: ['fileBaseName'],
  },
};

export const DATA_CATALOG_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.DATA_CATALOG_DATASET,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['datasetId', 'dataFrameName'],
  datasetId: {displayName: 'Dataset', configShapePath: ['datasetId']},
  dataFrameName: {displayName: 'Table name', configShapePath: ['dataFrameName']},
};

export const CONNECTION_DATA_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CONNECTION_DATA_DATASET,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionId', 'connectionFileId', 'transformDataType', 'dataFrameName'],
  connectionId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionId'],
    connectionId: {
      displayName: 'Connection',
      [CUSTOM_COMPONENT_KEY]: ConnectionSelector,
    },
  },
  connectionFileId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionFileId'],
    connectionFileId: {
      displayName: 'Connection file',
      [CUSTOM_COMPONENT_KEY]: ConnectionFileSelector,
    },
  },
  transformDataType: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['transformDataType'],
    transformDataType: {
      displayName: 'Data type',
      [CUSTOM_COMPONENT_KEY]: ConnectionTransformDataTypeSelector,
    },
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Table name',
      configShapePath: ['dataFrameName'],
    },
  },
};

const UPLOAD_CSV_MAX_FILE_SIZE_BYTES = 150 * 1024 * 1024;

export const UPLOAD_CSV_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.UPLOAD_CSV_DATASET,
  0
> = {
  // fileName is omitted from FIELD_ORDER because FileUploadEditor sets it automatically when a file is uploaded.
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['s3URL', 'dataFrameName'],
  s3URL: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['s3URL'],
    s3URL: {
      displayName: 'Upload CSV file',
      configShapePath: ['s3URL'],
      editorOverride: StepConfigEditorOverride.FILE_UPLOAD,
      fieldSettings: {
        fileNamePath: ['fileName'],
        accept: '.csv',
        maxFileSizeBytes: UPLOAD_CSV_MAX_FILE_SIZE_BYTES,
      },
    },
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Table name',
      configShapePath: ['dataFrameName'],
    },
  },
};

export const INITIALIZE_DATASET_V2_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.INITIALIZE_DATASET,
  2
> = {
  // In V2, fields not in FIELD_ORDER are automatically hidden.
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['dashboardId', 'dataFrameName'],
  dashboardId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dashboardId'],
    dashboardId: {
      displayName: 'Dashboard and query',
      [CUSTOM_COMPONENT_KEY]: DashboardBlockEditor,
    },
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Table name',
      configShapePath: ['dataFrameName'],
    },
  },
};

export const LOOK_UP_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.LOOK_UP, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName', 'lookupConfig', 'schemaId'],
  lookupConfig: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['lookupConfig'],
    lookupConfig: {
      [CUSTOM_COMPONENT_KEY]: LookUpConfigSectionEditor,
    },
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Name table',
      configShapePath: ['dataFrameName'],
    },
  },
  schemaId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['schemaId'],
    schemaId: {
      displayName: BenchlingConfig.DOMAIN_GRAPH_ENABLE_EXPERIMENTAL_SCHEMAS
        ? 'Select entity, plate or container schema'
        : 'Select entity schema',
      infoText:
        "Choose the specific schema that matches the type of items in your lookup column. If the schema doesn't match your data, the step may fail or return no results.",
      [CUSTOM_COMPONENT_KEY]: LookUpFieldEditor,
    },
  },
};

export const JOIN_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.JOIN, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['join', 'dataFrameName'],
  join: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['join'],
    join: {[CUSTOM_COMPONENT_KEY]: JoinEditor},
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Name table',
      configShapePath: ['dataFrameName'],
    },
  },
};

export const UNION_DATASETS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.UNION_DATASETS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['union', 'includeDuplicates', 'dataFrameName'],
  union: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['union'],
    union: {[CUSTOM_COMPONENT_KEY]: UnionEditor},
  },
  includeDuplicates: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['includeDuplicates'],
    includeDuplicates: {
      displayName: 'Include duplicate rows',
      configShapePath: ['includeDuplicates'],
    },
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Name table',
      configShapePath: ['dataFrameName'],
    },
  },
};

export const DEFINE_TEMPLATE_VARIABLES_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['variables'],
  variables: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['variables'],
    variables: {
      displayName: '',
      [CUSTOM_COMPONENT_KEY]: DefineTemplateVariablesEditor,
    },
  },
};

export const REGISTRY_SCHEMA_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.REGISTRY_SCHEMA_DATASET,
  0
> = {
  // queryDescription and dataFrameName are omitted from FIELD_ORDER (hidden in V2).
  // dataFrameName is managed internally by RegistrySchemaQueryEditor.
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['query'],
  query: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['query'],
    query: {
      displayName: 'Registry schema query',
      [CUSTOM_COMPONENT_KEY]: RegistrySchemaQueryEditor,
    },
  },
};

export const TABLE_VIEW_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.TABLE_VIEW, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['viewName'],
  viewName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['viewName'],
    viewName: {
      displayName: 'Table name',
      configShapePath: ['viewName'],
    },
  },
};

export const DATASET_VIEW_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.DATASET_VIEW, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [],
};

export const PLATE_SCHEMA_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.PLATE_SCHEMA_DATASET,
  0
> = {
  // queryDescription, plateIds, and dataFrameName are omitted from FIELD_ORDER (hidden in V2).
  // plateIds and dataFrameName are managed internally by PlateSchemaQueryEditor.
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['query'],
  query: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['query'],
    query: {
      displayName: 'Plate schema query',
      [CUSTOM_COMPONENT_KEY]: PlateSchemaQueryEditor,
    },
  },
};

function OpentronsRuntimeParametersEditor({
  config,
  configShape,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const runtimeParamsShape = getConfigShapeAtConfigShapePath(['runtimeParameters'], configShape, config);

  if (runtimeParamsShape.kind === 'shape') {
    const shapeWithMeta = runtimeParamsShape as Record<string, unknown>;
    const fieldOrder =
      (shapeWithMeta.__fieldOrder as Array<string> | undefined) ?? Object.keys(runtimeParamsShape.fields);
    const displayNames = (shapeWithMeta.__displayNames as Record<string, string> | undefined) ?? {};

    if (fieldOrder.length === 0) {
      return <></>;
    }

    return (
      <ShapeEditor
        configShape={configShape}
        config={config}
        onChangeConfig={onChangeConfig}
        disabled={disabled}
        uiSchema={{
          [FIELD_ORDER_UI_SCHEMA_KEY]: fieldOrder.map((name) => `runtimeParameters_${name}`),
          ...Object.fromEntries(
            fieldOrder.map((name) => [
              `runtimeParameters_${name}`,
              {
                displayName: displayNames[name] ?? name,
                configShapePath: ['runtimeParameters', name],
              },
            ])
          ),
        }}
      />
    );
  }

  if (runtimeParamsShape.kind === 'array' && Array.isArray(config.runtimeParameters)) {
    return (
      <ArrayEditor
        config={config}
        configShape={runtimeParamsShape}
        onChangeConfig={onChangeConfig}
        disabled={disabled}
        configShapePath={['runtimeParameters']}
        elementUISchema={{
          [FIELD_ORDER_UI_SCHEMA_KEY]: ['name', 'value'],
          [NUM_COLUMNS_KEY]: 2,
          name: {
            displayName: 'Parameter',
            configShapePath: ['name'],
          },
          value: {
            displayName: 'Value',
            configShapePath: ['value'],
          },
        }}
      />
    );
  }

  return <></>;
}

export const RUN_OPENTRONS_PROTOCOL_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.RUN_OPENTRONS_PROTOCOL,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionId', 'protocolId', 'runtimeParameters'],
  [BACKEND_FIELD_GENERATOR_CONFIG]: {triggerFields: ['connectionId', 'protocolId']},
  connectionId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionId'],
    connectionId: {
      displayName: 'Connection',
      infoText: 'The instrument connection to the Opentrons robot.',
      configShapePath: ['connectionId'],
    },
  },
  protocolId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['protocolId'],
    protocolId: {
      displayName: 'Protocol',
      infoText:
        'The Opentrons protocol to run. Selecting a protocol will populate any available runtime parameters.',
      configShapePath: ['protocolId'],
    },
  },
  runtimeParameters: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['runtimeParameters'],
    runtimeParameters: {
      displayName: 'Runtime parameters',
      infoText: 'Parameters defined by the selected protocol. Values are sent to the robot at run time.',
      [CUSTOM_COMPONENT_KEY]: OpentronsRuntimeParametersEditor,
    },
  },
};
